import React, { useEffect, useState, useMemo } from "react";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import moment from "moment";
import momentTz from "moment-timezone";
import { textTranslate } from "../../../../utils/i18n";

const NetworkInfoChart = ({ data, networkProtocol }) => {
  const plotBands =
    networkProtocol === "mqtt" || networkProtocol === "http"
      ? [
          {
            // Excellent
            from: 30,
            to: 100,
            color: "rgba(00, 250, 0, 0.1)",
            label: {
              text: "Excellent",
              style: {
                color: "#00cc00",
              },
            },
          },
          {
            // Good
            from: 1,
            to: 30,
            color: "rgba(150, 250, 0, 0.1)",
            label: {
              text: "Good",
              style: {
                color: "#cccc00",
              },
            },
          },
          {
            // Weak
            from: 0,
            to: 1,
            color: "rgba(250, 250, 0, 0.1)",
            label: {
              text: "Weak",
              style: {
                color: "#999900",
              },
            },
          },
          {
            // Bad
            from: 0,
            to: -190,
            color: "rgba( 100,0, 0, 0.1)",
            label: {
              text: "Bad",
              style: {
                color: "#cc0000",
              },
            },
          },
        ]
      : [
          {
            // Excellent
            from: 0,
            to: -100,
            color: "rgba(00, 250, 0, 0.1)",
            label: {
              text: "Excellent",
              style: {
                color: "#00cc00",
              },
            },
          },
          {
            // Good
            from: -100,
            to: -125,
            color: "rgba(150, 250, 0, 0.1)",
            label: {
              text: "Good",
              style: {
                color: "#cccc00",
              },
            },
          },
          {
            // Weak
            from: -125,
            to: -130,
            color: "rgba(250, 250, 0, 0.1)",
            label: {
              text: "Weak",
              style: {
                color: "#999900",
              },
            },
          },
          {
            // Bad
            from: -130,
            to: -190,
            color: "rgba( 100,0, 0, 0.1)",
            label: {
              text: "Bad",
              style: {
                color: "#cc0000",
              },
            },
          },
        ];
  const [chartConfigs, setChartConfigs] = useState({
    chart: {
      type: "line",
      zoomType: "x",
      panning: true,
      panKey: "shift",
      spacingTop: 30,
      height: 550,
      scrollablePlotArea: {
        minHeight: 400,
      },
    },

    title: {
      text: " ",
    },

    subtitle: {
      text: " ",
    },

    yAxis: {
      title: {
        text: " ",
      },

      crosshair: true,
      plotBands,
    },

    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
    },

    xAxis: {
      crosshair: true,
      type: "datetime",
      tickPixelInterval: 50,
      dateTimeLabelFormats: {
        millisecond: "%H:%M:%S.%L",
        second: "%H:%M:%S",
        minute: "%H:%M",
        hour: "%H:%M",
        day: "%e. %b",
        week: "%e. %b",
        month: "%b '%y",
        year: "%Y",
      },
    },
    tooltip: {
      formatter: function () {
        let output = [];
        output.push(`${moment(this.x).format("LLL")}`);
        this.points.forEach((point) => {
          output.push(`<br/> <b>${point.series.name}: </b> ${point.y}`);
        });
        if ("seqNumber" in this.points[0].point)
          output.push(`<br/> <b>${textTranslate("sequenceNumber")}: </b> ${this.points[0].point.seqNumber}`);
        if ("transmissionSeqNumber" in this.points[0].point && this.points[0].point.transmissionSeqNumber != null)
          output.push(`<br/> <b>${textTranslate("transmissionSeqNumber")}: </b> ${this.points[0].point.transmissionSeqNumber}`);

        return output.join("");
      },
      shared: true,
      crosshairs: true,
    },
    time: {
      time: "",
      getTimezoneOffset: function (timestamp) {
        var zone = momentTz.tz.guess(),
          timezoneOffset = -moment.tz(timestamp, zone).utcOffset();
        return timezoneOffset;
      },
    },

    credits: {
      enabled: false,
    },
    lang: {
      loading: "Loading...",
      months: moment.months(),
      weekdays: moment.weekdays(),
      shortMonths: moment.monthsShort(),
      exportButtonTitle: textTranslate("exportButtonTitle"),
      printButtonTitle: textTranslate("printButtonTitle"),
      rangeSelectorFrom: textTranslate("rangeSelectorFrom"),
      rangeSelectorTo: textTranslate("rangeSelectorTo"),
      rangeSelectorZoom: textTranslate("rangeSelectorZoom"),
      downloadPNG: textTranslate("downloadPNG"),
      downloadJPEG: textTranslate("downloadJPEG"),
      downloadPDF: textTranslate("downloadPDF"),
      downloadSVG: textTranslate("downloadSVG"),
      viewFullscreen: textTranslate("viewFullscreen"),
    },

    exporting: {
      showTable: false,
      csv: {
        dateFormat: "%Y-%m-%d %H:%M:%S",
      },
      filename: "iot.klimator.dk_network_strength",
      buttons: {
        contextButton: {
          menuItems: [
            "viewFullscreen",
            "printChart",
            "separator",
            "downloadPNG",
            "downloadJPEG",
            "downloadSVG",
            "separator",
            "downloadPDF",
            "downloadCSV",
            "downloadXLS",
          ],
        },
      },
    },
  });

  const formatSeriesData = () => {
    const rssiSeries = [];
    const rsrqSeries = [];
    data.forEach((d) => {
      const { rssi, rsrq, time, seqNumber, transmissionSeqNumber } = d;
      const t = moment(time).unix() * 1000;
      rssiSeries.push({ x: t, y: rssi, seqNumber: seqNumber, transmissionSeqNumber: transmissionSeqNumber, name: "RSSI" });
      if (rsrq) rsrqSeries.push({ x: t, y: rsrq, seqNumber: seqNumber, name: "RSRQ" });
    });
    const series = [
      {
        name: "RSSI",
        data: rssiSeries,
        color: "#28436B",
        // rssiSeries holds point objects (with seqNumber/transmissionSeqNumber), not plain
        // [x, y] pairs. Above Highcharts' default turboThreshold (1000 points), turbo parsing
        // only understands plain arrays and silently drops the whole series instead. Disable it.
        turboThreshold: 0,
      },
    ];
    if (rsrqSeries && rsrqSeries.length > 0) {
      series.push({
        name: "RSRQ",
        data: rsrqSeries,
        color: "#6B5028",
        turboThreshold: 0,
      });
    }
    return series;
  };
  useEffect(() => {
    setChartConfigs((prev) => ({ ...prev, series: [...formatSeriesData()] }));
  }, [data]);

  return useMemo(() => <HighchartsReact highcharts={Highcharts} options={chartConfigs} />);
};

export default NetworkInfoChart;
